import { useEffect, useState, useMemo, useCallback, useRef, useReducer } from 'react';
import { motion } from 'motion/react';
import {
  getFirstNodeAfterStart,
  nextNodeId,
  dialogueLine,
  dialogueAnswerLabels,
  getPremiumAddonForAnswer,
  getAutoAdvancePlan,
  skipAfterPremiumChain,
  findUpstreamDialogueNodeId,
  getEntryLive2dAnimNodeId,
  describeLive2dAnimEntryNode,
  findLive2dAnimNodeById,
  reduceAnimGraphOnFlowTrigger,
  reduceAnimGraphOnDialogueAggregateEmotion,
  reduceAnimGraphOnMotionComplete,
  resolveTriggerFromCatalog,
  live2dAnimNodeHasDoneOut
} from './flowRunner';
import { relationshipTierIndex } from './relationshipLevels';
import {
  applyRpDeltaToCharactersAndSets,
  buildRelationshipRewardDispatchDetail
} from './relationshipRpApply';
import { patchUserState, fetchSceneByFlowId, fetchTriggers } from './gameSessionApi';
import FlowSceneRenderer from './ui/FlowSceneRenderer';
import GameDialogueBar from './ui/GameDialogueBar';
import GameChoiceButtons from './ui/GameChoiceButtons';
import GamePreviewShell from './ui/GamePreviewShell';
import { characterItems } from './fixtures/uiKitFixtures';
import { snappyTween } from './ui/motionUi';
import { replacePlayerNamePlaceholder } from './chatTextPlaceholders';
import {
  explainAggregateEmotionMatch,
  isDialogueEmotionDebugEnabled,
  normalizeForDialogueEmotionTags
} from './dialogueEmotionAggregates';

function animLive2dPlaybackReducer(state, action) {
  switch (action.type) {
    case 'reset': {
      const ag = action.animGraph;
      if (!ag?.nodes?.length) return { nodeId: null, pending: [], clipReplayNonce: 0 };
      const entry = getEntryLive2dAnimNodeId(ag.nodes);
      return { nodeId: entry, pending: [], clipReplayNonce: 0 };
    }
    case 'flowTrigger': {
      const next = reduceAnimGraphOnFlowTrigger(action.animGraph, state, action.triggerId);
      return { nodeId: next.nodeId, pending: next.pending, clipReplayNonce: 0 };
    }
    case 'dialogueAggregateEmotion': {
      const next = reduceAnimGraphOnDialogueAggregateEmotion(
        action.animGraph,
        state,
        action.aggregateKey
      );
      return { nodeId: next.nodeId, pending: next.pending, clipReplayNonce: 0 };
    }
    case 'motionComplete': {
      if (!action.animGraph?.nodes?.length || !state.nodeId) return state;
      const hasDone = live2dAnimNodeHasDoneOut(action.animGraph, state.nodeId);
      if (!hasDone) {
        return {
          nodeId: state.nodeId,
          pending: state.pending,
          clipReplayNonce: (state.clipReplayNonce || 0) + 1
        };
      }
      const next = reduceAnimGraphOnMotionComplete(action.animGraph, state);
      return {
        nodeId: next.nodeId,
        pending: next.pending,
        clipReplayNonce: 0
      };
    }
    default:
      return state;
  }
}

function isExistingFlowNodeId(nodes, id) {
  if (id == null || id === '') return false;
  const s = String(id).trim();
  if (!s) return false;
  return (nodes || []).some((n) => String(n.id) === s);
}

/**
 * @param {object} props
 * @param {object|null} props.flow
 * @param {'ru'|'en'} props.activeLang
 * @param {(meta?: { controlSessionHistory?: string }) => void} props.onExit — при субмиссивном треке передаётся сводка сессии для After Control (как Unity ControlHer → AIChat).
 * @param {number} props.playerTokens
 * @param {() => void} [props.onInsufficientTokens]
 * @param {object} [props.gameSettings]
 * @param {string} props.playerId
 * @param {object|null} props.session — актуальный user state (characters, kinks, inventory, tokens)
 * @param {string|null} props.playCharacterId — null = интро без персонажа
 * @param {(u: object) => void} [props.onUserStateUpdated]
 * @param {(visible: boolean) => void} [props.onLive2dPolicy] — false = скрыть Live2D (slide)
 * @param {string} [props.characterName]
 * @param {Array<{id:string,name:string,price:number,icon?:string}>|null} [props.flowGiftCatalog] — null = загрузка; из API (type Gift)
 * @param {boolean} [props.trackControlSessionSummary] — копить реплики/выборы/подарки для After Control Session (только субмиссивный персонаж)
 * @param {string} [props.summaryCharacterName] — имя для строк «(You)»; по умолчанию characterName
 * @param {string} [props.summaryPlayerNickname] — ник для «(Player)»; по умолчанию из session.nickname
 * @param {(fn: (() => void) | null) => void} [props.registerCharacterFlowExit] — передать наружу `exitFlow` (кнопка Exit должна вызывать его, чтобы в After Control ушла накопленная sessionHistory).
 * @param {Array} [props.setsCatalog] — каталог сетов для автo-разблокировки альбомов по RP
 */
export default function FlowPlayback({
  flow,
  activeLang,
  onExit,
  playerTokens,
  onInsufficientTokens,
  gameSettings,
  playerId,
  session,
  playCharacterId,
  onUserStateUpdated,
  onLive2dPolicy,
  characterName = '',
  flowGiftCatalog = null,
  flowItemCatalog = [],
  trackControlSessionSummary = false,
  summaryCharacterName,
  summaryPlayerNickname,
  registerCharacterFlowExit,
  setsCatalog = [],
  onJoiSession
}) {
  const nodes = useMemo(() => flow?.nodes ?? [], [flow]);
  const edges = useMemo(() => flow?.edges ?? [], [flow]);
  const [nodeId, setNodeId] = useState(null);
  const [linePhase, setLinePhase] = useState('typing');
  const [lineTick, setLineTick] = useState(0);
  const [bgUrl, setBgUrl] = useState(null);
  /** Обычный фон флоу vs slide (в 16:9 как сцена, поверх персонажа). */
  const [bgAssetKind, setBgAssetKind] = useState(null);
  const autoConsumed = useRef(false);
  const dialogueRef = useRef(null);
  const lastDialogueIdRef = useRef(null);
  /** Дедуп: одна эмоция на строку (flowId|nodeId|текст), без гонки lineTick/linePhase. */
  const lastDialogueAggregateEmoteRef = useRef('');
  /**
   * Сброс anim-графа на entry только при смене флоу / контроллера / версии графа (updatedAt).
   * Иначе каждый новый объект animGraph с API заново вызывал reset и откатывал эмоции к entry.
   */
  const lastAnimGraphResetGateRef = useRef('');
  /** Закрытие окна подарков без покупки увеличивает счётчик — отменяет async run() после await (иначе уходит в ветку «купил»). */
  const giftShopVisitGenerationRef = useRef(0);
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const [skinModal, setSkinModal] = useState(null);
  const [giftModal, setGiftModal] = useState(null);
  const [flowScene, setFlowScene] = useState(null);
  const [triggersCatalog, setTriggersCatalog] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchTriggers();
        if (!cancelled) setTriggersCatalog(Array.isArray(rows) ? rows : []);
      } catch (e) {
        console.warn('[FlowPlayback] triggers catalog', e);
        if (!cancelled) setTriggersCatalog([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const live2dAnimCtrl = flowScene?.characterId?.live2dAnimControllerId;
  const live2dAnimGraph = live2dAnimCtrl?.animGraph;
  const animGraphRuntime = useMemo(() => {
    const raw = live2dAnimGraph;
    if (!raw?.nodes?.length) return null;
    return { nodes: raw.nodes, edges: raw.edges || [] };
  }, [live2dAnimGraph]);

  const [animLive2d, dispatchAnimLive2d] = useReducer(animLive2dPlaybackReducer, {
    nodeId: null,
    pending: [],
    clipReplayNonce: 0
  });

  /** Строки в стиле Unity sessionHistory для After Control Session */
  const controlSessionLinesRef = useRef([]);
  const controlDialogueLoggedRef = useRef(new Set());
  /** Один выход из флоу → один onExit (иначе несколько advanceTo(null) подряд шлют follow-up несколько раз). */
  const flowExitFiredRef = useRef(false);

  const youLabel = String(summaryCharacterName ?? characterName ?? '').trim() || 'Character';
  const playerLabel = String(summaryPlayerNickname ?? session?.nickname ?? '').trim() || 'Player';

  const pushControlSessionLine = useCallback(
    (line) => {
      if (!trackControlSessionSummary) return;
      const s = String(line || '').trim();
      if (!s) return;
      controlSessionLinesRef.current.push(s);
    },
    [trackControlSessionSummary]
  );

  useEffect(() => {
    controlSessionLinesRef.current = [];
    controlDialogueLoggedRef.current = new Set();
    lastDialogueAggregateEmoteRef.current = '';
  }, [flow?._id, trackControlSessionSummary]);

  useEffect(() => {
    lastAnimGraphResetGateRef.current = '';
  }, [flow?._id]);

  const hasCharacter = Boolean(playCharacterId);
  const charRow = useMemo(() => {
    if (!playCharacterId) return null;
    return (session?.characters || []).find((c) => String(c.characterId) === String(playCharacterId));
  }, [session?.characters, playCharacterId]);

  const ctx = useMemo(
    () => ({
      hasCharacter,
      sessionNumber: Number(charRow?.sessionNumber) || 0,
      relationshipTier: relationshipTierIndex(Number(charRow?.relationship) || 0, gameSettings)
    }),
    [hasCharacter, charRow?.sessionNumber, charRow?.relationship, gameSettings]
  );

  useEffect(() => {
    onLive2dPolicy?.(true);
    return () => onLive2dPolicy?.(true);
  }, [onLive2dPolicy]);

  useEffect(() => {
    if (!flow) return;
    flowExitFiredRef.current = false;
    const first = getFirstNodeAfterStart(nodes, edges);
    setNodeId(first);
    setBgUrl(null);
    setBgAssetKind(null);
    setLinePhase('typing');
    autoConsumed.current = false;
    lastDialogueIdRef.current = null;
    setSkinModal(null);
    setGiftModal(null);
  }, [flow, nodes, edges]);

  useEffect(() => {
    const fid = flow?._id;
    if (!fid) {
      setFlowScene(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const doc = await fetchSceneByFlowId(String(fid));
        if (!cancelled) setFlowScene(doc);
      } catch (e) {
        console.error(e);
        if (!cancelled) setFlowScene(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [flow?._id]);

  useEffect(() => {
    const flowId = String(flow?._id || '');
    const ctrlId = String(live2dAnimCtrl?._id || '');
    const graphVersion = String(live2dAnimCtrl?.updatedAt || '');
    const gate = `${flowId}|${ctrlId}|${graphVersion}`;

    if (!animGraphRuntime) {
      lastAnimGraphResetGateRef.current = '';
      console.info('[FlowPlayback] Live2D anim graph: отключён (нет animGraph на персонаже сцены или пустой nodes)');
      dispatchAnimLive2d({ type: 'reset', animGraph: null });
      return;
    }

    if (lastAnimGraphResetGateRef.current === gate) {
      return;
    }
    lastAnimGraphResetGateRef.current = gate;

    const entryDbg = describeLive2dAnimEntryNode(animGraphRuntime.nodes);
    console.info('[FlowPlayback] Live2D anim graph — стартовая нода (сброс позиции в графе)', {
      gate,
      flowId: flow?._id,
      characterName: flowScene?.characterId?.name,
      animControllerName: live2dAnimCtrl?.name,
      animControllerId: live2dAnimCtrl?._id,
      ...entryDbg
    });
    dispatchAnimLive2d({ type: 'reset', animGraph: animGraphRuntime });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gate по flow/controller/updatedAt; имена только для лога
  }, [animGraphRuntime, flow?._id, live2dAnimCtrl?._id, live2dAnimCtrl?.updatedAt]);

  const onAnimMotionFinished = useCallback(() => {
    if (!animGraphRuntime) return;
    dispatchAnimLive2d({ type: 'motionComplete', animGraph: animGraphRuntime });
  }, [animGraphRuntime]);

  const animLive2dClip = useMemo(() => {
    if (!animGraphRuntime || !animLive2d.nodeId) return null;
    const n = findLive2dAnimNodeById(animGraphRuntime.nodes, animLive2d.nodeId);
    if (!n || n.type !== 'live2dAnim' || !String(n.data?.modelPath || '').trim()) return null;
    return {
      modelPath: String(n.data.modelPath).trim(),
      motionGroup: n.data.motionGroup || 'idle',
      motionIndex: n.data.motionIndex ?? 0,
      /** В рантайме графа не зацикливаем: motionFinish → переход или повтор клипа. */
      motionLoop: false,
      graphNodeId: String(animLive2d.nodeId),
      /** Счётчик повтора одного и того же стейта без ребра `done`. */
      replayNonce: animLive2d.clipReplayNonce || 0
    };
  }, [animGraphRuntime, animLive2d.nodeId, animLive2d.clipReplayNonce]);

  const node = useMemo(() => nodes.find((n) => n.id === nodeId), [nodes, nodeId]);

  useEffect(() => {
    autoConsumed.current = false;
    if (node?.type === 'dialogue') {
      lastDialogueIdRef.current = nodeId;
      setLinePhase('typing');
      setLineTick((k) => k + 1);
    }
  }, [nodeId, node?.type]);

  useEffect(() => {
    if (!trackControlSessionSummary || node?.type !== 'dialogue' || nodeId == null) return;
    const id = String(nodeId);
    if (controlDialogueLoggedRef.current.has(id)) return;
    controlDialogueLoggedRef.current.add(id);
    const raw = dialogueLine(node, activeLang);
    const text = replacePlayerNamePlaceholder(raw, playerLabel).trim();
    if (text) {
      pushControlSessionLine(`- ${youLabel} (You): ${text}`);
    }
  }, [
    nodeId,
    node,
    activeLang,
    trackControlSessionSummary,
    youLabel,
    playerLabel,
    pushControlSessionLine
  ]);

  const exitFlow = useCallback(() => {
    if (flowExitFiredRef.current) return;
    flowExitFiredRef.current = true;
    if (trackControlSessionSummary) {
      onExit({
        controlSessionHistory: controlSessionLinesRef.current.join('\n')
      });
    } else {
      onExit();
    }
  }, [onExit, trackControlSessionSummary]);

  useEffect(() => {
    if (typeof registerCharacterFlowExit !== 'function') return undefined;
    registerCharacterFlowExit(() => exitFlow());
    return () => {
      registerCharacterFlowExit(null);
    };
  }, [registerCharacterFlowExit, exitFlow]);

  const advanceTo = useCallback(
    (next) => {
      if (!next) exitFlow();
      else setNodeId(next);
    },
    [exitFlow]
  );

  useEffect(() => {
    if (!nodeId || !node) return;
    if (node.type === 'dialogue') return;
    if (skinModal || giftModal) return;
    if (autoConsumed.current) return;

    const plan = getAutoAdvancePlan(nodes, edges, nodeId, ctx);
    let cancelled = false;

    const run = async () => {
      autoConsumed.current = true;
      try {
        switch (plan.kind) {
          case 'stop':
            exitFlow();
            return;
          case 'background': {
            if (plan.hideLive2d) onLive2dPolicy?.(false);
            else onLive2dPolicy?.(true);
            if (plan.url) {
              setBgUrl(plan.url);
              setBgAssetKind(plan.assetKind === 'slide' ? 'slide' : 'background');
            } else {
              setBgUrl(null);
              setBgAssetKind(null);
            }
            if (cancelled) return;
            advanceTo(plan.nextId);
            return;
          }
          case 'immediate':
            if (cancelled) return;
            advanceTo(plan.nextId);
            return;
          case 'relationship': {
            const cid = String(playCharacterId);
            const snap = sessionRef.current || {};
            const delta = Number(plan.delta) || 0;
            const { characters: chars, sets: mergedSets, prevRp, nextRp, newlyUnlockedIds } =
              applyRpDeltaToCharactersAndSets(
                snap.characters || [],
                snap.sets || [],
                cid,
                delta,
                setsCatalog
              );
            const res = await patchUserState({
              playerId,
              characters: chars,
              sets: mergedSets
            });
            if (cancelled) return;
            if (res?.userState) onUserStateUpdated?.(res.userState);
            if (delta !== 0) {
              const detail = buildRelationshipRewardDispatchDetail(
                prevRp,
                nextRp,
                gameSettings,
                newlyUnlockedIds,
                setsCatalog
              );
              window.dispatchEvent(new CustomEvent('game-flow-relationship-up', { detail }));
            }
            advanceTo(plan.nextId);
            return;
          }
          case 'kink': {
            if (!plan.kinkId || !playerId) {
              advanceTo(plan.nextId);
              return;
            }
            const snap = sessionRef.current || {};
            const cur = [...(snap.kinks || [])];
            if (!cur.includes(plan.kinkId)) cur.push(plan.kinkId);
            const res = await patchUserState({ playerId, kinks: cur });
            if (cancelled) return;
            if (res?.userState) onUserStateUpdated?.(res.userState);
            advanceTo(plan.nextId);
            return;
          }
          case 'trigger': {
            const trig = resolveTriggerFromCatalog(plan.info, triggersCatalog);
            const trigKey = String(
              trig.triggerKey || trig.triggerId || trig.triggerName || ''
            ).trim();
            if (trackControlSessionSummary) {
              if (trigKey === 'not_enough_relationship') {
                pushControlSessionLine(`- ${youLabel} (You): [Not enough relationship]`);
              }
            }
            if (trigKey === 'joi_session') {
              console.info('[FlowPlayback] joi_session trigger');
              onJoiSession?.();
            }
            console.info('[Flow Trigger]', trig);
            const tid = trig.triggerId;
            if (animGraphRuntime && tid) {
              dispatchAnimLive2d({
                type: 'flowTrigger',
                animGraph: animGraphRuntime,
                triggerId: tid
              });
            }
            advanceTo(plan.nextId);
            return;
          }
          case 'skin_shop': {
            autoConsumed.current = false;
            setSkinModal({
              skinType: plan.skinType,
              nextBought: plan.nextBought,
              nextDeclined: plan.nextDeclined
            });
            return;
          }
          case 'gift_shop': {
            autoConsumed.current = false;
            setGiftModal({
              items: plan.items,
              nextNone: plan.nextNone,
              nextOther: plan.nextOther,
              nextByItemIndex: plan.nextByItemIndex,
              /** Снимок диалога до Switch — после закрытия магазина без покупки */
              returnToDialogueId: lastDialogueIdRef.current || null
            });
            return;
          }
          default:
            advanceTo(plan.nextId);
        }
      } catch (e) {
        console.error(e);
        advanceTo(plan.nextId);
      }
    };

    const t = window.setTimeout(run, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [
    nodeId,
    node,
    nodes,
    edges,
    ctx,
    advanceTo,
    exitFlow,
    playerId,
    playCharacterId,
    charRow,
    gameSettings,
    onUserStateUpdated,
    onLive2dPolicy,
    skinModal,
    giftModal,
    trackControlSessionSummary,
    pushControlSessionLine,
    youLabel,
    setsCatalog,
    animGraphRuntime,
    triggersCatalog
  ]);

  const answers = node?.type === 'dialogue' ? node.data?.answers || [] : [];
  const labels = node?.type === 'dialogue' ? dialogueAnswerLabels(node, activeLang) : [];
  const playerNick = String(session?.nickname ?? '').trim();
  const dialogueDisplayTextForAnim = useMemo(
    () =>
      node?.type === 'dialogue'
        ? replacePlayerNamePlaceholder(dialogueLine(node, activeLang), playerNick)
        : '',
    [node, activeLang, playerNick]
  );
  useEffect(() => {
    if (node?.type !== 'dialogue' || nodeId == null) return;

    const rawLine = dialogueDisplayTextForAnim;
    const normalizedLine = normalizeForDialogueEmotionTags(rawLine);
    const hasStar = normalizedLine.includes('*');
    const explained = explainAggregateEmotionMatch(rawLine);

    if (!hasStar && !explained) return;

    if (!animGraphRuntime) {
      if (explained) {
        console.warn(
          '[DialogueEmotion] Распознан тег',
          explained.keyword,
          '→',
          explained.aggregateKey,
          'но animGraph нет: у персонажа сцены не привязан Live2D Anim Controller, или сцена ещё не подгрузилась.'
        );
      } else {
        console.warn(
          '[DialogueEmotion] В тексте есть «звёздочки», но тег не из списка (*happy*, *shy*, *shocked* …).',
          { preview: normalizedLine.slice(0, 120) }
        );
      }
      return;
    }

    // lineTick растёт при каждом входе в ноду dialogue — иначе тот же flow|nodeId|текст после цикла флоу
    // давал бы один contentKey и вторая/третья эмоция на ту же реплику не диспатилась.
    const contentKey = `${flow?._id ?? ''}|${nodeId}|${lineTick}|${rawLine}`;
    if (lastDialogueAggregateEmoteRef.current === contentKey) {
      if (isDialogueEmotionDebugEnabled()) {
        console.info('[DialogueEmotion] уже обработана эта строка', { contentKey });
      }
      return;
    }

    if (!explained) {
      console.warn(
        '[DialogueEmotion] В тексте есть *, но тег не из списка (*happy*, *shy*, …).',
        { preview: normalizedLine.slice(0, 120) }
      );
      return;
    }

    const agg = explained.aggregateKey;
    lastDialogueAggregateEmoteRef.current = contentKey;
    console.info('[DialogueEmotion] эмоция из текста →', agg, `(совпало: ${explained.keyword})`);
    if (isDialogueEmotionDebugEnabled()) {
      console.info('[DialogueEmotion] dispatch detail', { aggregateKey: agg, contentKey });
    }
    dispatchAnimLive2d({
      type: 'dialogueAggregateEmotion',
      animGraph: animGraphRuntime,
      aggregateKey: agg
    });
  }, [
    animGraphRuntime,
    flow?._id,
    node?.type,
    nodeId,
    lineTick,
    dialogueDisplayTextForAnim,
    dispatchAnimLive2d
  ]);

  const showChoices = node?.type === 'dialogue' && linePhase === 'done' && answers.length > 0;
  const token = playerTokens ?? 0;

  const handleDialogueContinue = useCallback(() => {
    const next = nextNodeId(nodes, edges, nodeId, null);
    advanceTo(skipAfterPremiumChain(nodes, edges, next));
  }, [nodes, edges, nodeId, advanceTo]);

  const handleChoose = useCallback(
    (id) => {
      const idx = parseInt(id, 10);
      if (trackControlSessionSummary && node?.type === 'dialogue') {
        const labs = dialogueAnswerLabels(node, activeLang);
        const label = replacePlayerNamePlaceholder(labs[idx] || '…', playerLabel).trim();
        if (label) {
          pushControlSessionLine(`- ${playerLabel} (Player): ${label}`);
        }
      }
      const raw = nextNodeId(nodes, edges, nodeId, idx);
      advanceTo(skipAfterPremiumChain(nodes, edges, raw));
    },
    [
      trackControlSessionSummary,
      node,
      activeLang,
      playerLabel,
      pushControlSessionLine,
      nodes,
      edges,
      nodeId,
      advanceTo
    ]
  );

  const inv = session?.inventory || [];
  /** Полноэкранный фон ноды Background (не slide): при сцене не показываем — только slide в кадре сцены. */
  const showFlowNodeBg = Boolean(bgUrl) && !flowScene && bgAssetKind !== 'slide';
  /** Slide без привязанной сцены — тот же 16:9, что и сцена. */
  const showSlideLetterbox = Boolean(bgUrl) && bgAssetKind === 'slide' && !flowScene;
  const sceneSlideUrl =
    flowScene && bgAssetKind === 'slide' && bgUrl ? bgUrl : null;

  const flowLayer = (
    <div className="pointer-events-none absolute inset-0 z-30 flex flex-col">
      {showFlowNodeBg ? (
        <>
          <div
            className="absolute inset-0 z-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${bgUrl})` }}
            aria-hidden
          />
          <div className="absolute inset-0 z-[1] bg-black/55" aria-hidden />
        </>
      ) : null}
      {showSlideLetterbox ? (
        <div className="absolute inset-0 z-0 flex items-center justify-center bg-black">
          <div className="relative aspect-video w-full max-w-[min(100vw,calc(100dvh*16/9))] overflow-hidden">
            <img
              src={bgUrl}
              alt=""
              draggable={false}
              className="block h-full w-full object-contain object-center"
            />
          </div>
        </div>
      ) : null}
      <div className="relative z-10 flex min-h-0 flex-1 flex-col pointer-events-none">
        {node?.type === 'dialogue' ? (
          <>
            {!showChoices ? (
              <button
                type="button"
                className="pointer-events-auto absolute inset-0 z-[5] cursor-pointer border-0 bg-transparent p-0"
                aria-label={linePhase === 'typing' ? 'Show full line' : 'Continue'}
                onClick={() => dialogueRef.current?.advance()}
              />
            ) : null}
            {showChoices ? (
              <>
                <div
                  className="pointer-events-auto absolute inset-0 z-[18] bg-transparent"
                  aria-hidden
                />
                <div className="pointer-events-none absolute inset-0 z-[19] flex items-center justify-end p-4 pr-5 min-[480px]:pr-10 md:pr-14 lg:pr-20">
                  <div className="pointer-events-auto w-[min(100%,28rem)] max-w-md shrink-0">
                    <GameChoiceButtons
                      sectionTitle={null}
                      choices={answers.map((a, i) => {
                        const base =
                          typeof a.tokenCost === 'number' && Number.isFinite(a.tokenCost) && a.tokenCost > 0
                            ? a.tokenCost
                            : 0;
                        const prem = getPremiumAddonForAnswer(nodes, edges, nodeId, i);
                        return {
                          id: String(i),
                          label: replacePlayerNamePlaceholder(labels[i] || '…', playerNick),
                          tokenCost: base + prem > 0 ? base + prem : undefined
                        };
                      })}
                      playerTokens={token}
                      onInsufficientTokens={onInsufficientTokens}
                      onChoose={handleChoose}
                    />
                  </div>
                </div>
              </>
            ) : null}
            <div className="relative z-[25] mt-auto shrink-0 pointer-events-none">
              <GameDialogueBar
                ref={dialogueRef}
                text={replacePlayerNamePlaceholder(dialogueLine(node, activeLang), playerNick)}
                speakerLabel={(characterName && String(characterName).trim()) || 'Narration'}
                lineKey={`${nodeId}-${lineTick}`}
                onLineComplete={() => setLinePhase('done')}
                onContinue={answers.length === 0 ? handleDialogueContinue : undefined}
              />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );

  return (
    <>
      {flowScene ? (
        <FlowSceneRenderer
          scene={flowScene}
          slideOverlayUrl={sceneSlideUrl}
          animLive2dClip={animLive2dClip}
          onAnimMotionFinished={animGraphRuntime ? onAnimMotionFinished : undefined}
        />
      ) : null}
      {flowLayer}
      {skinModal ? (
        <div className="pointer-events-auto fixed inset-0 z-[70]">
          <FlowSkinShopOverlay
            characterName={characterName}
            skinType={skinModal.skinType}
            playerTokens={token}
            inventory={inv}
            flowItemCatalog={flowItemCatalog}
            onInsufficientTokens={onInsufficientTokens}
            overlayZClass="z-[70]"
            onCloseDeclined={() => {
              const d = skinModal.nextDeclined;
              setSkinModal(null);
              autoConsumed.current = false;
              if (isExistingFlowNodeId(nodes, d)) {
                advanceTo(String(d).trim());
                return;
              }
              console.warn(
                '[FlowPlayback] Skin shop closed/declined: no valid "declined" edge target',
                d
              );
              advanceTo(null);
            }}
            onPurchasedOrOwned={async (itemId, price) => {
              if (!playerId) return;
              const snap = sessionRef.current || {};
              const curInv = [...(snap.inventory || [])];
              if (!curInv.includes(itemId)) curInv.push(itemId);
              const tok = Math.max(0, Number(snap.tokens) || 0);
              const pay = Math.max(0, Number(price) || 0);
              try {
                const res = await patchUserState({
                  playerId,
                  inventory: curInv,
                  tokens: pay > 0 ? tok - pay : tok
                });
                if (res?.userState) onUserStateUpdated?.(res.userState);
              } catch (e) {
                console.error(e);
              }
              const b = skinModal.nextBought;
              setSkinModal(null);
              autoConsumed.current = false;
              if (isExistingFlowNodeId(nodes, b)) {
                advanceTo(String(b).trim());
                return;
              }
              console.warn(
                '[FlowPlayback] Skin purchased but no valid "bought" edge target',
                b
              );
              advanceTo(null);
            }}
          />
        </div>
      ) : null}
      {giftModal ? (
        <div className="pointer-events-auto fixed inset-0 z-[70]">
          <FlowGiftShopOverlay
            switchItems={giftModal.items}
            catalogGifts={flowGiftCatalog}
            playerTokens={token}
            onInsufficientTokens={onInsufficientTokens}
            overlayZClass="z-[70]"
            onPickGift={({ wiredItemIndex, price, giftName }) => {
              const pay = Math.max(0, Number(price) || 0);
              if (pay > token) {
                onInsufficientTokens?.();
                return;
              }
              const visitGen = giftShopVisitGenerationRef.current;
              const run = async () => {
                if (visitGen !== giftShopVisitGenerationRef.current) return;
                if (pay > 0) {
                  if (!playerId) return;
                  const snap = sessionRef.current || {};
                  const tok = Math.max(0, Number(snap.tokens) || 0);
                  if (pay > tok) {
                    onInsufficientTokens?.();
                    return;
                  }
                  if (visitGen !== giftShopVisitGenerationRef.current) return;
                  try {
                    const res = await patchUserState({ playerId, tokens: tok - pay });
                    if (res?.userState) onUserStateUpdated?.(res.userState);
                  } catch (e) {
                    console.error(e);
                    onInsufficientTokens?.();
                    return;
                  }
                }
                if (visitGen !== giftShopVisitGenerationRef.current) return;
                if (trackControlSessionSummary && giftName) {
                  const gn = String(giftName).trim() || 'Gift';
                  pushControlSessionLine(`- ${playerLabel} (Player): [Gift: ${gn}]`);
                }
                /*
                 * Item Switch (только если подарок куплен в ЭТОМ открытии окна):
                 * 1) конкретный item-N → nextByItemIndex
                 * 2) иной подарок из каталога → nextOther
                 * Закрытие окна без покупки → onCloseNone (none / назад к выбору), не сюда.
                 */
                const next =
                  wiredItemIndex != null
                    ? giftModal.nextByItemIndex(wiredItemIndex)
                    : giftModal.nextOther;
                if (!isExistingFlowNodeId(nodes, next)) {
                  console.warn('[FlowPlayback] Gift bought but target node missing for edge');
                  setGiftModal(null);
                  autoConsumed.current = false;
                  return;
                }
                if (visitGen !== giftShopVisitGenerationRef.current) return;
                setGiftModal(null);
                autoConsumed.current = false;
                advanceTo(String(next).trim());
              };
              void run();
            }}
            onCloseNone={() => {
              giftShopVisitGenerationRef.current += 1;
              const none = giftModal.nextNone;
              const returnTo =
                giftModal.returnToDialogueId != null && giftModal.returnToDialogueId !== ''
                  ? giftModal.returnToDialogueId
                  : lastDialogueIdRef.current;
              const switchNodeId = nodeId;
              setGiftModal(null);
              autoConsumed.current = false;
              /* 3) None — не купили в этом открытии; 4) нет None — назад к dialogue + выбор */
              if (isExistingFlowNodeId(nodes, none)) {
                advanceTo(String(none).trim());
                return;
              }
              const fromGraph =
                switchNodeId && isExistingFlowNodeId(nodes, switchNodeId)
                  ? findUpstreamDialogueNodeId(nodes, edges, switchNodeId)
                  : null;
              const dialogueBack = [returnTo, fromGraph].find((id) => isExistingFlowNodeId(nodes, id));
              if (dialogueBack) {
                setNodeId(String(dialogueBack).trim());
                setLinePhase('done');
                return;
              }
              console.warn(
                '[FlowPlayback] Gift shop closed: no "none" edge and no upstream dialogue found for switch',
                switchNodeId
              );
              autoConsumed.current = true;
            }}
          />
        </div>
      ) : null}
    </>
  );
}

function FlowSkinShopOverlay({
  characterName,
  skinType,
  playerTokens,
  inventory,
  flowItemCatalog = [],
  onInsufficientTokens,
  onCloseDeclined,
  onPurchasedOrOwned,
  overlayZClass = 'z-[60]'
}) {
  const items = useMemo(
    () => {
      const dbItems = flowItemCatalog.filter(
        (it) => (it.itemType || '').toLowerCase() === String(skinType).toLowerCase()
      );
      if (dbItems.length > 0) return dbItems;
      return characterItems.filter(
        (it) => (it.itemType || '').toLowerCase() === String(skinType).toLowerCase()
      );
    },
    [skinType, flowItemCatalog]
  );
  const ownedHere = items.some((it) => inventory.includes(it.id));

  return (
    <GamePreviewShell title={`Items · ${skinType}`} onClose={onCloseDeclined} overlayZClass={overlayZClass}>
      <div className="p-3 sm:p-4">
        <p className="mb-3 text-center text-sm text-game-muted">{characterName}</p>
        {ownedHere ? (
          <div className="space-y-3">
            <p className="text-center text-sm text-game-text">Already in inventory — continue.</p>
            <motion.button
              type="button"
              transition={snappyTween}
              whileTap={{ scale: 0.98 }}
              className="w-full rounded-xl bg-game-accent py-3 text-sm font-bold text-game-accentText"
              onClick={() => {
                const first = items.find((it) => inventory.includes(it.id));
                onPurchasedOrOwned(first?.id || items[0]?.id, 0);
              }}
            >
              Continue
            </motion.button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {items.length === 0 ? (
              <p className="col-span-full text-center text-sm text-game-muted">No items for this type.</p>
            ) : (
              items.map((it) => (
                <motion.button
                  key={it.id}
                  type="button"
                  whileTap={{ scale: 0.99 }}
                  className="flex flex-col items-center rounded-2xl border border-white/10 bg-game-surface/80 p-4 text-left"
                  onClick={() => {
                    const cost = Number(it.price) || 0;
                    if (cost > playerTokens) {
                      onInsufficientTokens?.();
                      return;
                    }
                    onPurchasedOrOwned(it.id, cost);
                  }}
                >
                  <span className="text-3xl">{it.emoji}</span>
                  <span className="mt-2 text-sm font-semibold text-game-text">{it.name}</span>
                  <span className="mt-1 text-xs font-bold text-game-accent">{it.price} ◆</span>
                </motion.button>
              ))
            )}
          </div>
        )}
        <motion.button
          type="button"
          transition={snappyTween}
          whileTap={{ scale: 0.98 }}
          className="mt-4 w-full rounded-xl border border-white/15 py-2.5 text-sm font-semibold text-game-muted"
          onClick={onCloseDeclined}
        >
          Decline
        </motion.button>
      </div>
    </GamePreviewShell>
  );
}

function giftIconIsImageUrl(icon) {
  const s = String(icon || '').trim();
  if (!s) return false;
  return (
    s.startsWith('http://') ||
    s.startsWith('https://') ||
    s.startsWith('/') ||
    s.startsWith('data:')
  );
}

function FlowGiftShopOverlay({
  switchItems,
  catalogGifts,
  playerTokens,
  onInsufficientTokens,
  onPickGift,
  onCloseNone,
  overlayZClass = 'z-[60]'
}) {
  const wiredItemIndexById = useMemo(() => {
    const m = new Map();
    (switchItems || []).forEach((row, i) => {
      if (row?.itemId == null || row.itemId === '') return;
      m.set(String(row.itemId), i);
    });
    return m;
  }, [switchItems]);

  return (
    <GamePreviewShell title="Gifts" onClose={onCloseNone} overlayZClass={overlayZClass}>
      <div className="max-h-[min(70vh,420px)] space-y-2 overflow-y-auto p-3 sm:p-4">
        {catalogGifts == null ? (
          <p className="py-8 text-center text-sm text-game-muted">Loading gifts…</p>
        ) : catalogGifts.length === 0 ? (
          <p className="text-center text-sm text-game-muted">No gifts available.</p>
        ) : (
          catalogGifts.map((g) => {
            const price = Number(g.price) || 0;
            const wiredItemIndex = wiredItemIndexById.get(String(g.id));
            const icon = String(g.icon || '').trim();
            return (
              <motion.button
                key={g.id}
                type="button"
                whileTap={{ scale: 0.99 }}
                className="flex w-full items-center gap-3 rounded-xl border border-white/10 bg-game-surface/70 px-3 py-2.5 text-left"
                onClick={() => {
                  if (price > playerTokens) {
                    onInsufficientTokens?.();
                    return;
                  }
                  onPickGift({
                    wiredItemIndex: wiredItemIndex !== undefined ? wiredItemIndex : null,
                    price,
                    giftName: g.name || 'Gift'
                  });
                }}
              >
                {giftIconIsImageUrl(icon) ? (
                  <img
                    src={icon}
                    alt=""
                    className="h-9 w-9 shrink-0 rounded-lg object-cover"
                  />
                ) : (
                  <span className="text-2xl">{icon || '🎁'}</span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-game-text">{g.name || 'Gift'}</p>
                  <p className="text-xs text-game-accent">{price} ◆</p>
                </div>
              </motion.button>
            );
          })
        )}
        <motion.button
          type="button"
          whileTap={{ scale: 0.98 }}
          className="w-full rounded-xl py-2 text-sm text-game-muted"
          onClick={onCloseNone}
        >
          Close
        </motion.button>
      </div>
    </GamePreviewShell>
  );
}
